import {
  getAuthState,
  getGCPRequestState,
  IStoreState,
} from "../../rootReducer";
import { IGcpAppDispatch } from "../../store";
import config from "../../../config";
import { performSignedRequest } from "../auth/asyncActions";
import {
  addInstalledFlightMarkerToState,
  convertFromGCPRequestV2ToGcpRequest,
  createInstalledFlightMarker,
  ILatLng,
  persistGcpRequest,
  removeInstalledFlightMarkerFromState,
} from "./reducer";

import {
  IGCPRequestV2,
  PlacementStatus,
} from "../../../apiFacades/bapi/gcpRequests";
import currentGcpRequestStore from "../../../react/ModeCurrentGCPRequest/currentGCPRequestStore";
import { canViewExistingControls, getPayloadFromToken } from "../auth/utils";

export const fetchCurrentGcpRequest =
  (customerId: string, siteId: string) =>
  async (dispatch: IGcpAppDispatch, getState: () => IStoreState) => {
    const authState = getAuthState(getState());
    const { ACL } = getPayloadFromToken(authState.accessToken);
    const isPilot = canViewExistingControls(ACL);

    const response = await dispatch(
      performSignedRequest(
        {
          method: "GET",
          url: `${config.endpoints.backofficeApi}/${customerId}/${siteId}/gcp-requests`,
          params: { limit: 1 },
        },
        customerId,
        siteId,
      ),
    );

    const gcpRequest =
      response.data.length > 0 &&
      (response.data[0] as IGCPRequestV2 | false | undefined);

    if (gcpRequest) {
      if (gcpRequest.status === "CLOSED") {
        throw new Error(
          "There are currently no open GCP requests, which means no GCP actions are needed for this site right now. 🎉",
        );
      }

      // @TODO MESS ALERT: Keep this above the Redux store update.
      // We save GCP Request into Zustand
      // Then generate SendCoordinates and AddNew names by mutating objects in Zustand store
      // Then save the original object to Redux
      // When we press Download we use the Redux store, not Zustand!
      // Because we mutate the objects the Redux store gets updated as well
      const currentGcpRequestStoreState = currentGcpRequestStore.getState();
      currentGcpRequestStoreState.setGCPRequest(gcpRequest);

      // Converting to the old data format so that the existing stores and logic would work
      // without huge refactoring
      dispatch(
        persistGcpRequest(
          convertFromGCPRequestV2ToGcpRequest(gcpRequest, isPilot),
        ),
      );
    } else {
      throw new Error(
        "We couldn't get the current GCP request data. Please try again later.",
      );
    }
  };

export const fetchScanGcpRequest =
  (customerId: string, siteId: string, scanDate: string) =>
  async (dispatch: IGcpAppDispatch) => {
    const bapiResponce = await dispatch(
      performSignedRequest(
        {
          method: "GET",
          url: `${config.endpoints.backofficeApi}/${customerId}/${siteId}/${scanDate}/gcp-request`,
        },
        customerId,
        siteId,
      ),
    );

    const gcpRequest = bapiResponce.data.gcpRequest;

    if (
      !gcpRequest.suggestedFlightMarkers.length &&
      !Object.keys(gcpRequest.corruptedFlightMarkers).length
    ) {
      throw new Error("Empty Scan GCP Request received");
    }
    dispatch(persistGcpRequest(gcpRequest));
  };

export const changeFlightMarkerPlacementStatus =
  (
    customerId: string,
    siteId: string,
    id: string,
    placementStatus: PlacementStatus,
    coordinates?: ILatLng, // to support the existing UI logic without refactoring
  ) =>
  async (dispatch: IGcpAppDispatch) => {
    if (placementStatus === PlacementStatus.COMPLETED) {
      const installedFlightMarker = createInstalledFlightMarker(
        id,
        coordinates,
      );

      dispatch(addInstalledFlightMarkerToState(installedFlightMarker));
    } else if (placementStatus === PlacementStatus.UNCOMPLETED) {
      dispatch(removeInstalledFlightMarkerFromState(id));
    }

    await dispatch(
      performSignedRequest(
        {
          method: "PUT",
          url: `${config.endpoints.backofficeApi}/${customerId}/${siteId}/gcp-request/gcp/${id}/placement-status`,
          data: {
            placementStatus,
          },
        },
        customerId,
        siteId,
      ),
    );
  };

export const addInstalledFlightMarker =
  (
    customerId: string,
    siteId: string,
    scanDate: string,
    id: string,
    coordinates: ILatLng,
  ) =>
  async (dispatch: IGcpAppDispatch) => {
    const installedFlightMarker = createInstalledFlightMarker(id, coordinates);
    dispatch(addInstalledFlightMarkerToState(installedFlightMarker));
    await dispatch(
      performSignedRequest(
        {
          method: "PUT",
          url: `${config.endpoints.backofficeApi}/${customerId}/${siteId}/${scanDate}/gcp-request/installed-flight-markers/${id}`,
          data: installedFlightMarker,
        },
        customerId,
        siteId,
      ),
    );
  };

export const removeInstalledFlightMarker =
  (customerId: string, siteId: string, scanDate: string, id: string) =>
  async (dispatch: IGcpAppDispatch) => {
    dispatch(removeInstalledFlightMarkerFromState(id));
    await dispatch(
      performSignedRequest(
        {
          method: "DELETE",
          url: `${config.endpoints.backofficeApi}/${customerId}/${siteId}/${scanDate}/gcp-request/installed-flight-markers/${id}`,
        },
        customerId,
        siteId,
      ),
    );
  };

export const updateScanGcpRequest =
  (customerId: string, siteId: string, scanDate: string) =>
  async (dispatch: IGcpAppDispatch, getState: () => IStoreState) => {
    const gcpRequest = getGCPRequestState(getState());
    await dispatch(
      performSignedRequest(
        {
          method: "PUT",
          url: `${config.endpoints.backofficeApi}/${customerId}/${siteId}/${scanDate}/gcp-request`,
          data: {
            gcpRequest,
          },
        },
        customerId,
        siteId,
      ),
    );
  };
